import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Sparkles, Moon, Flame, Zap, Music, Cloud, Rocket, Droplets, Check, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MOOD_OPTIONS = [
  { name: "Dark", color: "#6b21a8", icon: Moon, desc: "Haunting & moody" },
  { name: "Aggressive", color: "#dc2626", icon: Flame, desc: "Raw & hard-hitting" },
  { name: "Bouncy", color: "#f59e0b", icon: Zap, desc: "Upbeat grooves" },
  { name: "Melodic", color: "#ec4899", icon: Music, desc: "Beautiful melodies" },
  { name: "Chill", color: "#06b6d4", icon: Cloud, desc: "Smooth & relaxed" },
  { name: "Hype", color: "#16a34a", icon: Rocket, desc: "Get the crowd going" },
  { name: "Sad", color: "#3b82f6", icon: Droplets, desc: "Emotional & deep" },
  { name: "Ethereal", color: "#8b5cf6", icon: Sparkles, desc: "Dreamy soundscapes" },
];

const VibeOnboarding = ({ onComplete }) => {
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  const toggleMood = (mood) => {
    setSelected(prev =>
      prev.includes(mood) ? prev.filter(m => m !== mood) : prev.length < 4 ? [...prev, mood] : prev
    );
  };

  const handleSave = async () => {
    if (selected.length < 1) {
      toast.error("Pick at least 1 vibe!");
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/recommendations/onboarding`, { moods: selected }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("Vibes locked in!");
      onComplete(selected);
    } catch (e) {
      toast.error("Failed to save preferences");
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    localStorage.setItem("onboarding_skipped", "true");
    onComplete([]);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-md"
        data-testid="vibe-onboarding-modal"
      >
        <motion.div
          initial={{ scale: 0.9, y: 30 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 30 }}
          className="w-full max-w-lg mx-4 p-8 bg-zinc-900 border border-white/10"
        >
          <div className="text-center mb-8">
            <Sparkles className="w-10 h-10 text-neon-cyan mx-auto mb-3" />
            <h2 className="font-heading text-3xl uppercase tracking-tight mb-2">Pick Your Vibes</h2>
            <p className="text-zinc-400 text-sm">
              Select up to 4 moods to personalize your experience
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-8">
            {MOOD_OPTIONS.map((mood) => {
              const Icon = mood.icon;
              const isSelected = selected.includes(mood.name);
              return (
                <button
                  key={mood.name}
                  onClick={() => toggleMood(mood.name)}
                  className={`relative p-4 text-left transition-all ${
                    isSelected
                      ? "scale-[1.02]"
                      : "hover:scale-[1.01] opacity-70 hover:opacity-100"
                  }`}
                  style={{
                    background: isSelected ? `${mood.color}33` : `${mood.color}0a`,
                    border: `1px solid ${isSelected ? mood.color : mood.color + '22'}`,
                  }}
                  data-testid={`vibe-option-${mood.name.toLowerCase()}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className="w-5 h-5" style={{ color: mood.color }} />
                    <span className="font-heading text-sm uppercase tracking-wide" style={{ color: isSelected ? mood.color : '#a1a1aa' }}>
                      {mood.name}
                    </span>
                  </div>
                  <p className="text-xs text-zinc-600">{mood.desc}</p>
                  {isSelected && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute top-2 right-2 w-5 h-5 flex items-center justify-center"
                      style={{ background: mood.color }}
                    >
                      <Check className="w-3 h-3 text-white" />
                    </motion.div>
                  )}
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handleSkip}
              className="text-xs text-zinc-600 hover:text-zinc-400 transition-colors"
              data-testid="skip-onboarding"
            >
              Skip for now
            </button>
            <Button
              onClick={handleSave}
              disabled={selected.length < 1 || saving}
              className="flex-1 bg-neon-cyan text-black font-bold uppercase tracking-wider h-12"
              data-testid="save-vibes-btn"
            >
              {saving ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <ArrowRight className="w-5 h-5 mr-2" />
              )}
              Lock In ({selected.length}/4)
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default VibeOnboarding;
